import React, { useMemo, useState, useEffect } from 'react';

function TeamRankingRow({ team, rank, onTeamClick, isCurrentUser = false }) {
    // Parse display_name into team name and known as parts
    const parseTeamName = (displayName) => {
        if (!displayName) return { teamName: '', knownAs: '' };
        
        const match = displayName.match(/^(.*?)\s*\(([^)]+)\)$/);
        if (match) {
            return {
                teamName: match[1].trim(),
                knownAs: `(${match[2].trim()})`
            };
        }
        return { teamName: displayName, knownAs: '' };
    };
    
    const { teamName, knownAs } = parseTeamName(team.display_name);
    
    // Use standing image based on rank, looping through 1-4
    const standingImageIndex = ((rank - 1) % 4) + 1;
    const standingImageUrl = `/art/standings/standing${standingImageIndex}.jpg`;

    // Rank icon for top 3
    const rankIcon = rank <= 3 ? `${process.env.PUBLIC_URL}/art/standings/${rank}st.png`.replace('1stst', '1st').replace('2st', '2nd').replace('3st', '3rd') : null;

    const handleClick = () => {
        if (onTeamClick) {
            onTeamClick(team);
        }
    };

    const getOrdinalSuffix = (num) => {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) {
            return num + "ST";
        }
        if (j === 2 && k !== 12) {
            return num + "ND";
        }
        if (j === 3 && k !== 13) {
            return num + "RD";
        }
        return num + "TH";
    };

    // Responsive mobile detection using a state and resize event
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 600);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div 
            className="team-row"
            style={{
                display: isMobile ? 'flex' : 'flex',
                flexDirection: isMobile ? 'row' : 'row',
                alignItems: isMobile ? 'center' : 'stretch',
                position: 'relative',
                padding: 0,
                marginBottom: '18px',
                minHeight: isMobile ? '70px' : '140px',
                height: isMobile ? '70px' : '180px',
                backgroundColor: isCurrentUser ? '#2c5282' : '#4a5568',
                borderRadius: '18px',
                cursor: 'pointer',
                border: isCurrentUser ? '2px solid #63b3ed' : '2px solid transparent',
                boxShadow: isCurrentUser ? '0 2px 12px #63b3ed33' : '0 2px 8px #0002',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '700px',
                transition: 'background 0.2s',
            }}
            onClick={handleClick}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = isCurrentUser ? '#2d3748' : '#2d3748'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = isCurrentUser ? '#2c5282' : '#4a5568'}
        >
            {/* Standing image on left, full height, not in a box */}
            <div style={{
                minWidth: isMobile ? '28%' : '40%',
                width: isMobile ? '28%' : '40%',
                height: isMobile ? '100%' : '100%',
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                margin: 0,
                background: isMobile ? '#222' : undefined,
            }}>
                <img
                    src={standingImageUrl}
                    alt="Standing"
                    style={{
                        width: isMobile ? '100%' : '100%',
                        height: isMobile ? '90%' : '100%',
                        objectFit: isMobile ? 'contain' : 'cover',
                        display: 'block',
                        borderRadius: 0,
                        margin: 0,
                        padding: 0,
                        minHeight: 0,
                        maxHeight: '100%',
                        maxWidth: '100%',
                    }}
                />
            </div>
            {/* Main content (right side) */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: isMobile ? 'center' : 'center',
                position: 'relative',
                minWidth: 0,
                padding: isMobile ? '0 0 0 8px' : '24px 0 24px 0',
                height: '100%',
            }}>
                {/* Rank icon for top 3 */}
                {rankIcon && !isMobile && (
                    <img
                        src={rankIcon}
                        alt={`Rank ${rank}`}
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 60,
                            height: 70,
                            zIndex: 2,
                        }}
                    />
                )}
                {/* Rank icon for top 3 */}
                {rankIcon && isMobile && (
                    <img
                        src={rankIcon}
                        alt={`Rank ${rank}`}
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 50,
                            height: 60,
                            zIndex: 2,
                        }}
                    />
                )}
                {/* Rank text (upper left) */}
                <div style={{
                    position: 'absolute',
                    top: isMobile ? 2 : -10,
                    left: isMobile ? -102 : -285,
                    fontWeight: 'bold',
                    fontSize: isMobile ? '2.1rem' : '5.1rem',
                    color: '#fff6b6',
                    fontFamily: 'Bebas Neue, Arial, sans-serif',
                    letterSpacing: '1px',
                    zIndex: 2,
                    padding: isMobile ? 0 : '2px 8px',
                    textShadow: '0 1px 2px #0006',
                }}>
                    {getOrdinalSuffix(rank)}
                </div>
                {/* Name and points (right, stacked) - simplified for mobile */}
                {!isMobile && (
                  <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      height: '100%',
                      minWidth: 0,
                      marginRight: '80px',
                  }}>
                      <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                      }}>
                          <div style={{
                              color: '#3182ce',
                              fontFamily: 'Bebas Neue, Arial, sans-serif',
                              fontWeight: 'bold',
                              fontSize: '2.2rem',
                              lineHeight: '1.1',
                              marginBottom: '2px',
                              textShadow: '0 1px 2px #0006',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                          }}>
                              {teamName}
                              {isCurrentUser && (
                                  <span style={{
                                      marginLeft: '8px',
                                      fontSize: '0.8rem',
                                      color: '#63b3ed',
                                      fontWeight: 'normal',
                                  }}>
                                      (YOUR TEAM)
                                  </span>
                              )}
                          </div>
                          {knownAs && (
                              <div style={{
                                  color: '#3182ce',
                                  fontFamily: 'Bebas Neue, Arial, sans-serif',
                                  fontWeight: 'normal',
                                  fontSize: '1.6rem',
                                  lineHeight: '1.1',
                                  marginTop: '-2px',
                                  textShadow: '0 1px 2px #0006',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  opacity: 0.8,
                              }}>
                                  {knownAs}
                              </div>
                          )}
                      </div>
                      <div style={{
                          color: '#48bb78',
                          fontFamily: 'Bebas Neue, Arial, sans-serif',
                          fontWeight: 'bold',
                          fontSize: '4.1rem',
                          marginTop: '0',
                          textShadow: '0 1px 2px #0006',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                      }}>
                          {team.score || 0}PT
                      </div>
                  </div>
                )}
                {isMobile && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    height: '100%',
                    minWidth: 0,
                    marginLeft: 0,
                  }}>
                    <div style={{
                      marginLeft: '60px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                      <div style={{
                        color: '#3182ce',
                        fontFamily: 'Bebas Neue, Arial, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        lineHeight: '1.1',
                        marginBottom: 0,
                        textShadow: '0 1px 2px #0006',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      }}>{teamName}</div>
                      {knownAs && (
                        <div style={{
                          color: '#3182ce',
                          fontFamily: 'Bebas Neue, Arial, sans-serif',
                          fontWeight: 'normal',
                          fontSize: '0.9rem',
                          lineHeight: '1.1',
                          marginTop: '-1px',
                          textShadow: '0 1px 2px #0006',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                          opacity: 0.8,
                        }}>{knownAs}</div>
                      )}
                    </div>
                    <div style={{
                      marginLeft: '50px',
                      color: '#48bb78',
                      fontFamily: 'Bebas Neue, Arial, sans-serif',
                      fontWeight: 'bold',
                      fontSize: '1.5rem',
                      marginTop: 0,
                      textShadow: '0 1px 2px #0006',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    }}>{team.score || 0}PT</div>
                  </div>
                )}
            </div>
            {/* Responsive styles */}
            <style>{`
                @media (max-width: 600px) {
                    .team-row {
                        padding: 0 !important;
                        min-height: 70px !important;
                        height: 70px !important;
                        border-radius: 8px !important;
                    }
                    .team-row > div:first-child {
                        min-width: 28vw !important;
                        width: 28vw !important;
                        max-width: 40vw !important;
                        margin-right: 0 !important;
                    }
                    .team-row img[alt='Standing'] {
                        width: 100% !important;
                        height: 90% !important;
                        min-height: 0 !important;
                        max-height: 90% !important;
                        border-radius: 0 !important;
                    }
                    .team-row > div:last-child {
                        padding: 0 0 0 8px !important;
                    }
                }
                @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
            `}</style>
        </div>
    );
}

export default TeamRankingRow;